// Settings modal — team members + stage catalogue + stage defaults +
// (admin-only) job-status catalogue. Lists current members; the
// inline form (TeamModalForm) handles adds. DELETE on a member is a
// soft-delete when the server detects the member is referenced by
// jobs. The job-status section only renders for admins (matches the
// requireAdmin gate on the server mutation routes).
import { useEffect, useState } from "react";
import type { TeamMemberDto } from "../../../../../../shared/types/production-schedule";
import { useAuth } from "../../../../auth/useAuth";
import {
  createTeamMember,
  deleteTeamMember,
  useTeam,
} from "../hooks/useProductionApi";
import { TeamModalForm } from "./TeamModalForm";
import { StageDefaultsSection } from "./StageDefaultsSection";
import { StageConfigsSection } from "./StageConfigsSection";
import { JobStatusesSection } from "./JobStatusesSection";
import { CycleTimesSection } from "./CycleTimesSection";
import { WorkScheduleSection } from "./WorkScheduleSection";

interface Props {
  open: boolean;
  onClose(): void;
}

export function TeamModal({ open, onClose }: Props) {
  const { data, refetch, error } = useTeam();
  const { isAdmin } = useAuth();
  const [busyId, setBusyId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (open) void refetch();
  }, [open, refetch]);

  if (!open) return null;

  const members = data?.team ?? [];

  const removeMember = async (m: TeamMemberDto) => {
    if (!window.confirm(`Remove ${m.name}? Jobs assigned to them stay intact; the member becomes inactive if referenced.`)) {
      return;
    }
    setBusyId(m.id);
    setActionError(null);
    try {
      await deleteTeamMember(m.id);
      await refetch();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="modal-bg" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal modal-xl">
        <div className="modal-header">
          <h2>Settings</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="modal-body">
          {(error ?? actionError) && (
            <div style={{ color: "var(--bsp-red-fg)", fontSize: 12, marginBottom: 8 }}>
              {actionError ?? error}
            </div>
          )}
          <div className="team-list">
            {members.length === 0 && (
              <p style={{ color: "var(--bsp-fg-muted)", fontSize: 12 }}>
                No team members yet — add the first one below.
              </p>
            )}
            {members.map((m) => (
              <div key={m.id} className="team-row">
                <div className="team-avatar-edit" style={{ background: m.color }}>
                  {m.initials}
                </div>
                <div className="team-info">
                  <div className="team-name">{m.name}</div>
                  <div className="team-role">{m.role}</div>
                </div>
                <button
                  type="button"
                  className="btn-sm danger"
                  onClick={() => void removeMember(m)}
                  disabled={busyId === m.id}
                >
                  {busyId === m.id ? "…" : "Remove"}
                </button>
              </div>
            ))}
          </div>
          <TeamModalForm
            onAdd={async (input) => {
              await createTeamMember(input);
              await refetch();
            }}
          />
          <StageConfigsSection />
          <StageDefaultsSection team={members} />
          {isAdmin ? <JobStatusesSection /> : null}
          {isAdmin ? <CycleTimesSection /> : null}
          {isAdmin ? <WorkScheduleSection /> : null}
        </div>
        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}
